import React from "react";
import {
  CalendarDays,
  Check,
  CheckCircle2,
  Clock3,
  MapPin,
  Package,
  Plane,
  Ship,
  Truck,
  Warehouse,
} from "lucide-react";

const STEPS = [
  {
    key: "at_warehouse",
    label: "At warehouse",
    desc: "Received at origin warehouse",
    Icon: Warehouse,
  },
  {
    key: "in_transit",
    label: "In transit",
    desc: "Moving to Ghana",
    Icon: Ship,
  },
  {
    key: "arrived_ghana",
    label: "Arrived in Ghana",
    desc: "Cleared into Ghana",
    Icon: MapPin,
  },
  {
    key: "ready_for_pickup",
    label: "Ready for pickup",
    desc: "Ready for collection",
    Icon: Package,
  },
  {
    key: "out_for_delivery",
    label: "Out for delivery",
    desc: "On the way to you",
    Icon: Truck,
  },
  {
    key: "delivered",
    label: "Delivered",
    desc: "Shipment completed",
    Icon: CheckCircle2,
  },
];

const STATUS_COPY = {
  at_warehouse: {
    title: "At the warehouse",
    sub: "Your shipment has been received and is waiting for dispatch.",
    color: "#1d4ed8",
    bg: "#eaf3ff",
  },
  in_transit: {
    title: "On the way to Ghana",
    sub: "Your shipment is currently moving through the route.",
    color: "#7650b8",
    bg: "#f1edff",
  },
  arrived_ghana: {
    title: "Arrived in Ghana",
    sub: "Your shipment has reached Ghana and is being processed.",
    color: "#08745c",
    bg: "#e8f8f1",
  },
  ready_for_pickup: {
    title: "Ready for pickup",
    sub: "Your shipment is ready to be collected.",
    color: "#087b78",
    bg: "#e5f8f7",
  },
  out_for_delivery: {
    title: "Out for delivery",
    sub: "Your shipment is on its final journey to you.",
    color: "#a15d05",
    bg: "#fff5dc",
  },
  delivered: {
    title: "Delivered",
    sub: "This shipment has been delivered successfully.",
    color: "#267342",
    bg: "#eaf7ed",
  },
};

function fmt(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString("en-GH", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function dateForStep(item, key) {
  if (key === "at_warehouse") return item.warehouse_arrival;
  if (key === "in_transit") return item.container_loaded_date;
  if (key === "arrived_ghana") return item.eta_ghana;
  return null;
}

export default function TrackCard({ item }) {
  const stepIdx = Math.max(
    0,
    STEPS.findIndex((step) => step.key === item.status),
  );
  const statusInfo = STATUS_COPY[item.status] || STATUS_COPY.at_warehouse;
  const CurrentIcon = STEPS[stepIdx].Icon;
  const progress = Math.round((stepIdx / (STEPS.length - 1)) * 100);
  const isAir = item.transport_mode === "air";

  return (
    <article className="tk-card tk-card-premium">
      <header className="tk-premium-header">
        <div>
          <p className="tk-card-kicker">SHIPMENT TRACKING</p>
          <h2 className="tk-item-name">{item.goods_name || "Shipment item"}</h2>
          <p className="tk-item-ref">
            {item.tracking_number
              ? `Tracking number: ${item.tracking_number}`
              : `Shipping mark: ${item.shipping_mark || "Not available"}`}
          </p>
        </div>
        <span className="tk-live-badge">
          <span /> Live
        </span>
      </header>

      <section
        className="tk-status-hero"
        style={{
          "--status-color": statusInfo.color,
          "--status-bg": statusInfo.bg,
        }}
      >
        <div className="tk-status-hero-icon">
          <CurrentIcon size={24} strokeWidth={1.8} />
        </div>
        <div className="tk-status-hero-copy">
          <p className="tk-status-overline">CURRENT STATUS</p>
          <h3>{statusInfo.title}</h3>
          <p>{statusInfo.sub}</p>
        </div>
        <div className="tk-progress-number">
          <strong>{progress}%</strong>
          <span>journey</span>
        </div>
      </section>

      <div className="tk-route-strip">
        <div className="tk-route-point">
          <span className="tk-route-flag">
            {isAir ? <Plane size={15} /> : <Ship size={15} />}
          </span>
          <div>
            <small>ORIGIN</small>
            <strong>{isAir ? "Air cargo" : "Warehouse"}</strong>
          </div>
        </div>
        <div className="tk-route-line">
          <span />
          <b>→</b>
          <span />
        </div>
        <div className="tk-route-point tk-route-point-end">
          <span className="tk-route-flag tk-route-flag-end">
            <MapPin size={15} />
          </span>
          <div>
            <small>DESTINATION</small>
            <strong>Ghana</strong>
          </div>
        </div>
      </div>

      <section className="tk-journey-section">
        <div className="tk-section-title-row">
          <div>
            <p className="tk-card-kicker">SHIPMENT JOURNEY</p>
            <h3>Where your package is now</h3>
          </div>
          <span>
            {stepIdx + 1} of {STEPS.length}
          </span>
        </div>
        <div className="tk-journey-list">
          {STEPS.map((step, index) => {
            const done = index < stepIdx;
            const active = index === stepIdx;
            const StepIcon = step.Icon;
            const date = dateForStep(item, step.key);
            return (
              <div
                key={step.key}
                className={`tk-journey-row${done ? " is-done" : ""}${active ? " is-active" : ""}`}
              >
                <div className="tk-journey-marker">
                  {done ? (
                    <Check size={13} strokeWidth={3} />
                  ) : (
                    <StepIcon size={15} strokeWidth={2} />
                  )}
                </div>
                <div className="tk-journey-copy">
                  <strong>{step.label}</strong>
                  <span>{step.desc}</span>
                </div>
                {date && <time>{fmt(date)}</time>}
              </div>
            );
          })}
        </div>
      </section>

      {(item.eta_ghana ||
        item.quantity ||
        item.weight_kg ||
        item.volume_cbm ||
        item.final_fee) && (
        <footer className="tk-facts-footer">
          {item.eta_ghana && (
            <div className="tk-fact">
              <CalendarDays size={16} />
              <span>
                <small>ESTIMATED ARRIVAL</small>
                <strong>{fmt(item.eta_ghana)}</strong>
              </span>
            </div>
          )}
          {item.quantity && (
            <div className="tk-fact">
              <Package size={16} />
              <span>
                <small>QUANTITY</small>
                <strong>{item.quantity} pcs</strong>
              </span>
            </div>
          )}
          {item.weight_kg && (
            <div className="tk-fact">
              <span>
                <small>WEIGHT</small>
                <strong>{item.weight_kg} kg</strong>
              </span>
            </div>
          )}
          {item.volume_cbm && parseFloat(item.volume_cbm) > 0 && (
            <div className="tk-fact">
              <span>
                <small>VOLUME</small>
                <strong>{item.volume_cbm} CBM</strong>
              </span>
            </div>
          )}
        </footer>
      )}
      <div className="tk-card-note">
        <Clock3 size={14} /> Status updates appear here as your shipment moves.
      </div>
    </article>
  );
}
